import React, { useState, useRef, useEffect } from 'react';

interface Option {
  value: string;
  label: string;
  station_id?: string | number;
  station_nom?: string;
}

interface Props {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  stations?: { id: number; nom: string }[];
  showStationFilter?: boolean;
  style?: React.CSSProperties;
}

const SearchableSelect: React.FC<Props> = ({ options, value, onChange, placeholder = '-- Choisir --', stations = [], showStationFilter = false, style }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [stationId, setStationId] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const filtered = options.filter(o => {
    const matchSearch = !search || o.label.toLowerCase().includes(search.toLowerCase());
    const matchStation = !stationId || String(o.station_id) === stationId;
    return matchSearch && matchStation;
  });

  const selectedLabel = options.find(o => o.value === value)?.label || '';

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', background: '#fff',
          cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          minHeight: 38, fontSize: 14
        }}
      >
        <span style={{ color: value ? '#333' : '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLabel || placeholder}
        </span>
        <span style={{ marginLeft: 8, fontSize: 10, color: '#999' }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: '#fff', border: '1px solid #ddd', borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 300, overflow: 'hidden',
          display: 'flex', flexDirection: 'column'
        }}>
          <div style={{ padding: 8, borderBottom: '1px solid #eee' }}>
            {showStationFilter && stations.length > 0 && (
              <select
                value={stationId}
                onChange={e => setStationId(e.target.value)}
                style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', marginBottom: 6, fontSize: 13 }}
                onClick={e => e.stopPropagation()}
              >
                <option value="">Toutes les stations</option>
                {stations.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
              </select>
            )}
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher..."
              onClick={e => e.stopPropagation()}
              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 220 }}>
            <div
              onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
              style={{ padding: '8px 12px', cursor: 'pointer', color: '#999', fontSize: 13, borderBottom: '1px solid #f5f5f5' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f0f4ff')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              {placeholder}
            </div>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: 13 }}>Aucun resultat</div>
            ) : filtered.map(o => (
              <div
                key={o.value}
                onClick={() => { onChange(o.value); setOpen(false); setSearch(''); }}
                style={{
                  padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                  background: o.value === value ? '#e8f0fe' : 'transparent',
                  fontWeight: o.value === value ? 600 : 400
                }}
                onMouseEnter={e => { if (o.value !== value) e.currentTarget.style.background = '#f0f4ff'; }}
                onMouseLeave={e => { if (o.value !== value) e.currentTarget.style.background = 'transparent'; }}
              >
                <div>{o.label}</div>
                {o.station_nom && <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{o.station_nom}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
